import { captureMessage } from 'src/lib/sentry';

interface Position {
  left: number;
  top: number;
}

interface PopupOptions {
  name?: string;
  width?: number;
  height?: number;
}

interface SubscribeOptions {
  name?: string;
}

export interface MessageData {
  [key: string]: unknown;
}

const centerPosition = (width: number, height: number): Position => ({
  left:
    (window.screenX < 0 ? window.screen.width + window.screenX : window.screenX) +
    (window.outerWidth - width) / 2,
  top: window.screenY + (window.outerHeight - height) / 2.5,
});

const generatePopupFeatures = (width: number, height: number): string => {
  const position = centerPosition(width, height);

  const features: string[] = [];
  features.push(`left=${position.left}`);
  features.push(`top=${position.top}`);
  features.push(`width=${width}`);
  features.push(`height=${height}`);
  features.push('scrollbars=1');
  features.push('toolbar=0');
  return features.join(',');
};

export const openPopup = (url: string, options?: PopupOptions): Window | null => {
  const { name, width, height } = options || {};

  // Prepare popup features and open it
  const features = generatePopupFeatures(width || 800, height || 600);
  return window.open(url, name, features);
};

export const subscribeToMessages = (
  callback: (data: MessageData) => void,
  options?: SubscribeOptions
): (() => void) => {
  function listener(event: MessageEvent) {
    try {
      // If we can't read source window of the message, then it's not our message
      if (!event.source) {
        return;
      }

      // Work only with messages from our popup
      if (options?.name && (event.source as Window).name !== options.name) {
        return;
      }

      callback(event.data);
    } catch (error) {
      captureMessage('Cannot subscribe to "window" messages', {
        level: 'warning',
        extra: {
          error: error instanceof Error ? error.toString() : String(error),
          source: 'popup -> subscribeToMessages',
        },
      });
    }
  }

  function unsubscribe(): void {
    window.removeEventListener('message', listener);
  }

  window.addEventListener('message', listener);

  return unsubscribe;
};
